import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon } from '@radix-ui/react-icons';
import type { SearchAutocompleteOption } from '@/shared/ui/components/form/SearchAutocomplete';
import { SectionCard } from '../SectionCard';
import { PropertyDialog } from '../PropertyDialog';
import { Text, DismissIconButton, UnstyledButton } from '@/shared/ui';
import ClientAvatar from '@/features/client-management/components/ClientAvatar';
import { getClientAvatarAccentColor } from '@/features/client-management/utils/avatar-accent';

interface BrokerCardProps {
  brokers: string[];
  memberOptions?: SearchAutocompleteOption[];
  onAssign?: (email: string) => Promise<void>;
  onRemove?: (email: string) => Promise<void>;
  saving?: boolean;
  /** `assignment.captured_by` — who sourced the listing. */
  capturedBy?: string;
  /** `assignment.external_captor`: a captor who is not in the house, by name. */
  externalCaptor?: string;
  /** `assignment.external_broker`: a broker outside the agency who works the property, by name. */
  externalBroker?: string;
  /** `assignment.primary_broker` — who answers for it. */
  primaryBroker?: string;
  /** Agency member names by e-mail; an address not in it is named from the e-mail. */
  memberNames?: Record<string, string>;
}

/**
 * Derives a human-readable display name from an email address.
 */
function getDisplayName(email: string): string {
  const name = email.split('@')[0] ?? email;
  return name
    .split(/[._-]/)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}

/**
 * Names what a broker does on this particular listing.
 *
 * The property already records who sourced it and who answers for it, so the
 * card says so instead of repeating the e-mail address under every name —
 * "who captou this" is the question a broker actually asks of this card.
 *
 * @param email - The assigned broker.
 * @param capturedBy - Agency member who sourced the listing.
 * @param primaryBroker - Agency member responsible for it.
 * @returns The i18n key for this broker's role.
 */
function roleKeyFor(email: string, capturedBy?: string, primaryBroker?: string): string {
  if (capturedBy && email === capturedBy) return 'detailView.brokers.roleCapturer';
  if (primaryBroker && email === primaryBroker) return 'detailView.brokers.rolePrimary';
  return 'detailView.brokers.roleBroker';
}

/**
 * Sidebar card listing responsible brokers, with assignment behind a dialog.
 *
 * @param props - The assigned brokers, assignable members and mutations.
 * @returns The rendered card.
 */
export const BrokerCard: React.FC<BrokerCardProps> = ({
  brokers,
  memberOptions = [],
  onAssign,
  onRemove,
  saving = false,
  capturedBy,
  externalCaptor,
  externalBroker,
  primaryBroker,
  memberNames,
}) => {
  const { t } = useTranslation('features/properties');
  const nameOf = (email: string) => memberNames?.[email] || getDisplayName(email);
  const [managing, setManaging] = useState(false);
  const canManage = Boolean(onAssign || onRemove);
  const availableOptions = useMemo(
    () => memberOptions.filter((m) => !brokers.includes(m.id)),
    [memberOptions, brokers],
  );

  // Three separate fields feed this card, and only one of them is the
  // assigned list. A lead broker or a capturer who was never *also*
  // assigned is still answerable for the listing, and showing only
  // `assigned_brokers` made them vanish from the one card that names who
  // is responsible. Lead first, since that is what the card is called.
  const people = useMemo(() => {
    const ordered: string[] = [];
    const add = (email?: string) => {
      if (email && !ordered.includes(email)) ordered.push(email);
    };
    add(primaryBroker);
    brokers.forEach(add);
    add(capturedBy);
    return ordered;
  }, [brokers, primaryBroker, capturedBy]);

  // A captor from outside the house has no account and no e-mail: the name on
  // file is shown as written, beside the members, and can't be managed here.
  const rows = useMemo(() => [
    ...people.map((email) => ({
      key: email,
      name: nameOf(email),
      role: t(roleKeyFor(email, capturedBy, primaryBroker)),
    })),
    ...(externalBroker
      ? [{
        key: `external-broker:${externalBroker}`,
        name: externalBroker,
        role: t('detailView.brokers.roleExternalBroker'),
      }]
      : []),
    ...(externalCaptor
      ? [{
        key: `external:${externalCaptor}`,
        name: externalCaptor,
        role: t('detailView.brokers.roleExternalCapturer'),
      }]
      : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [people, externalBroker, externalCaptor, capturedBy, primaryBroker, memberNames, t]);

  const title = rows.length === 1
    ? t('detailView.brokers.titleSingle')
    : t('detailView.brokers.titlePlural');

  return (
    <>
      <SectionCard
        title={title}
        data-testid="property-brokers-card"
        titleAccessory={
          canManage ? (
            <UnstyledButton
              type="button"
              onClick={() => setManaging(true)}
              data-testid="property-brokers-manage"
              className="whitespace-nowrap [font-weight:var(--font-semibold)] text-[var(--seed-base)] hover:underline underline-offset-2"
              style={{ fontSize: 'var(--text-meta)' }}
            >
              {t('detailView.brokers.manage')}
            </UnstyledButton>
          ) : undefined
        }
      >
        {rows.length === 0 ? (
          <div className="flex h-[60px] items-center justify-center text-[var(--text-tertiary)]" style={{ fontSize: 'var(--text-body-sm)' }}>
            {t('detailView.brokers.none')}
          </div>
        ) : (
          <div className="flex flex-col gap-[var(--space-2-5)]">
            {rows.map((person) => (
              <div key={person.key} className="flex items-center gap-[var(--space-2-5)]">
                {/* Same avatar the client list uses, colour and all: one
                    deterministic accent per person, so a card with three
                    people is three distinguishable faces rather than three
                    identical maroon discs. */}
                <ClientAvatar
                  fullName={person.name}
                  size={32}
                  accentColor={getClientAvatarAccentColor(person.name)}
                />
                <div className="flex min-w-0 flex-col">
                  <Text
                    component="span"
                    variant="bodySm"
                    c="var(--text-primary)"
                    className="break-words"
                  >
                    {person.name}
                  </Text>
                  <Text
                    component="span"
                    variant="meta"
                    c="var(--text-tertiary-soft)"
                  >
                    {person.role}
                  </Text>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {managing && (
        <PropertyDialog
          title={t('detailView.brokers.manageTitle')}
          subtitle={t('detailView.brokers.manageSubtitle')}
          width="520px"
          onClose={() => setManaging(false)}
          data-testid="property-brokers-dialog"
        >
          <div className="flex flex-col gap-[var(--space-2)]">
            {brokers.map((broker) => (
              <div
                key={broker}
                className="flex items-center gap-[var(--space-3)] rounded-(--radius-md) border border-[var(--border-ultra-subtle)] bg-[var(--surface-primary)] px-[var(--space-3)] py-[var(--space-2)]"
              >
                <div className="flex min-w-0 flex-1 flex-col">
                  <Text
                    component="span"
                    variant="bodySm"
                    c="var(--text-primary)"
                    className="truncate [font-weight:var(--font-medium)]"
                  >
                    {getDisplayName(broker)}
                  </Text>
                  <Text
                    component="span"
                    variant="meta"
                    c="var(--text-tertiary-soft)"
                    className="truncate"
                  >
                    {broker}
                  </Text>
                </div>
                {onRemove && (
                  <DismissIconButton
                    label={t('detailView.brokers.remove', { email: broker })}
                    disabled={saving}
                    onClick={() => { void onRemove(broker); }}
                  />
                )}
              </div>
            ))}

            {onAssign && availableOptions.length > 0 && (
              <div className="mt-[var(--space-2)] flex flex-col gap-[var(--space-2)]">
                <Text
                  component="span"
                  variant="meta"
                  c="var(--text-tertiary)"
                  className="[font-weight:var(--font-semibold)]"
                >
                  {t('detailView.brokers.select')}
                </Text>
                {availableOptions.map((option) => (
                  <UnstyledButton
                    key={option.id}
                    type="button"
                    disabled={saving}
                    onClick={() => { void onAssign(option.id); }}
                    data-testid="property-broker-option"
                    className="flex items-center gap-[var(--space-2)] rounded-(--radius-md) border border-dashed border-[var(--border-subtle)] px-[var(--space-3)] py-[var(--space-2)] text-left hover:bg-[var(--surface-hover)] disabled:opacity-50"
                  >
                    <PlusIcon width={14} height={14} className="flex-none text-(--seed-base)" />
                    <Text
                      component="span"
                      variant="bodySm"
                      c="var(--text-primary)"
                      className="min-w-0 flex-1 truncate"
                    >
                      {option.label}
                    </Text>
                  </UnstyledButton>
                ))}
              </div>
            )}
          </div>
        </PropertyDialog>
      )}
    </>
  );
};
